import { createApiClient } from '../../../../../api/apiClient';
import { buildOrderRulesRequest, unwrapApiPayload } from './requestBuilder';
import { OrderRulesChartResponse, OrderRulesDashboardResponse, OrderRulesFilterParams } from './types';

// ─── API: /api/order-rules/charts/max-potential ───────────────────────────────

export const fetchOrderRulesChartMaxPotential = async (
  params: OrderRulesFilterParams,
  locationIds: string[],
): Promise<OrderRulesChartResponse> => {
  const api = createApiClient();
  const body = buildOrderRulesRequest(params, locationIds);
  const res = await api.post<OrderRulesChartResponse>('/order-rules/charts/max-potential', body);
  return unwrapApiPayload<OrderRulesChartResponse>(res.data);
};

// ─── API: /api/order-rules/charts/enrollment-trend ───────────────────────────

export const fetchOrderRulesChartEnrollmentTrend = async (
  params: OrderRulesFilterParams,
  locationIds: string[],
): Promise<OrderRulesChartResponse> => {
  const api = createApiClient();
  const body = buildOrderRulesRequest(params, locationIds);
  const res = await api.post<OrderRulesChartResponse>('/order-rules/charts/enrollment-trend', body);
  return unwrapApiPayload<OrderRulesChartResponse>(res.data);
};

// ─── API: /api/order-rules/charts/layer-performance ──────────────────────────

export const fetchOrderRulesChartLayerPerformance = async (
  params: OrderRulesFilterParams,
  locationIds: string[],
): Promise<OrderRulesChartResponse> => {
  const api = createApiClient();
  const body = buildOrderRulesRequest(params, locationIds);
  const res = await api.post<OrderRulesChartResponse>('/order-rules/charts/layer-performance', body);
  return unwrapApiPayload<OrderRulesChartResponse>(res.data);
};

// ─── Normalizers: backend → legacy dashboard shape ───────────────────────────

export const normalizeMaxPotentialChartResponse = (
  data: OrderRulesChartResponse,
): Pick<OrderRulesDashboardResponse, 'maxPotentialExecution' | 'maxPotentialDrillLevel'> => ({
  maxPotentialDrillLevel: data.drillLevel ?? (data as any).data?.drillLevel,
  maxPotentialExecution: (data.dataPoints ?? [])
    .map((point) => {
      const geographyName = String(point.geographyName ?? point.region ?? point.label ?? '');
      const layerPerformancePct = Number(point.layerPerformancePct ?? point.layer_performance_pct ?? point.actualPct ?? point.actual_pct ?? 0);
      const gapPct = Number(point.gapPct ?? point.gap_pct ?? point.opportunityPct ?? point.opportunity_pct ?? 0);
      const maxPotentialPct = Number(point.maxPotentialPct ?? point.max_potential_pct ?? point.maxPotential ?? 0);
      const actualCases = Number(point.actualCases ?? point.actual_cases ?? point.actualVolume ?? point.actual_volume ?? point.actual ?? 0);
      const gapCases = Number(point.gapCases ?? point.gap_cases ?? point.opportunityCases ?? point.opportunity_cases ?? point.opportunityVolume ?? point.opportunity_volume ?? point.gap ?? 0);
      const maxPotentialCases = Number(point.maxPotentialCases ?? point.max_potential_cases ?? point.maxCases ?? point.max_cases ?? point.maxVolume ?? point.max_volume ?? (actualCases + gapCases));

      return {
        region: geographyName,
        geographyName,
        layerPerformancePct,
        gapPct,
        maxPotentialPct,
        actualCases,
        gapCases,
        maxPotentialCases,
      };
    })
    .sort((a, b) => b.maxPotentialPct - a.maxPotentialPct),
});

const extractPeriodLabel = (value: string): string => {
  if (!value) return '';

  const matches = value.match(/(?:\d{4}-)?([PW]\d+)$/i);
  if (matches?.[1]) {
    return matches[1].replace(/^P0+/, 'P').replace(/^W0+/, 'W');
  }

  const fallbackMatch = value.match(/([PW]\d+)/i);
  return fallbackMatch?.[1] ? fallbackMatch[1].replace(/^P0+/, 'P').replace(/^W0+/, 'W') : value;
};

export const normalizeEnrollmentTrendResponse = (
  data: OrderRulesChartResponse,
): Pick<OrderRulesDashboardResponse, 'enrollmentTrend' | 'enrollmentInsufficientDataMessage' | 'enrollmentTimeGrain' | 'enrollmentXAxisLabel' | 'enrollmentYAxisLabel'> => {
  const targetValue = Number((data as any).targetValue ?? (data as any).target_value ?? 75);

  return {
    enrollmentInsufficientDataMessage: data.insufficientDataMessage,
    enrollmentTimeGrain: data.timeGrain,
    enrollmentXAxisLabel: data.xaxisLabel,
    enrollmentYAxisLabel: data.yaxisLabel,
    enrollmentTrend: (data.dataPoints ?? []).map((point) => {
      const timePeriod = String(point.timePeriod ?? point.time_period ?? point.period ?? point.label ?? '');
      const year = extractPeriodLabel(timePeriod) || timePeriod;
      const enrolledPct = Number(
        point.enrollmentPct ?? point.enrollment_pct ?? point.enrolledPct ?? point.enrolled_pct ?? 0,
      );
      const targetPct = Number(point.targetPct ?? point.target_pct ?? targetValue);

      return {
        year,
        timePeriod,
        enrolledPct,
        targetPct,
        pointOverPointChange: point.pointOverPointChange ?? point.point_over_point_change ?? null,
      };
    }),
  };
};

export const normalizeLayerPerformanceResponse = (
  data: OrderRulesChartResponse,
): Pick<OrderRulesDashboardResponse, 'layerPerformanceTrend' | 'layerPerformanceInsufficientDataMessage' | 'layerPerformanceTimeGrain' | 'layerPerformanceXAxisLabel' | 'layerPerformanceYAxisLabel'> => ({
  layerPerformanceInsufficientDataMessage: data.insufficientDataMessage,
  layerPerformanceTimeGrain: data.timeGrain,
  layerPerformanceXAxisLabel: data.xaxisLabel,
  layerPerformanceYAxisLabel: data.yaxisLabel,
  layerPerformanceTrend: (data.dataPoints ?? []).map((point) => {
    const timePeriod = String(point.timePeriod ?? point.period ?? point.label ?? '');
    const label = extractPeriodLabel(timePeriod);
    const valuePct = Number(
      point.layerPerformancePct ?? point.layer_performance_pct ?? point.valuePct ?? point.value_pct ?? point.value ?? 0,
    );

    return {
      label,
      timePeriod,
      valuePct,
      pointOverPointChange: point.pointOverPointChange ?? point.point_over_point_change ?? null,
    };
  }),
});
